import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';

interface SearchContextType {
  searchText: string;
  hashtags: string[];
  content: string;
  setSearchText: (text: string) => void;
  handleSearch: (value: string) => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const SearchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [searchText, setSearchText] = useState<string>("");
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [content, setContent] = useState<string>("");
  const [debouncedValue, setDebouncedValue] = useState<string>("");

  // Debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(searchText);
    }, 600);

    return () => {
      clearTimeout(timer);
    };
  }, [searchText]);

  // Process search when debounced value changes
  useEffect(() => {
    // Split the search text into words
    const words = debouncedValue.split(' ');
    
    // Extract hashtags and content
    const newHashtags = words
      .filter(word => word.startsWith('#'));
    
    const newContent = words
      .filter(word => !word.startsWith('#'))
      .join(' ');

    setHashtags(newHashtags);
    setContent(newContent);
  }, [debouncedValue]);

  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  return (
    <SearchContext.Provider value={{
      searchText,
      hashtags,
      content,
      setSearchText,
      handleSearch
    }}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
}; 